
import React, { useState } from 'react';
import { Auth as SupabaseAuth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '@/integrations/supabase/client';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { signUpUser } from '@/services/auth-service';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getLogger } from '@/utils/logger';

// Create a logger specifically for the signup process
const log = getLogger({ module: 'SignUpForm' });

export const SignUpForm = () => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [ageVerified, setAgeVerified] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSignUp = async (event: React.FormEvent) => {
    event.preventDefault();
    
    log.info('Sign up attempt', { email, termsAccepted, ageVerified });
    
    if (!termsAccepted || !ageVerified) {
      const errorMsg = 'Please accept the terms and confirm your age to continue';
      log.warn('Sign up validation failed', { termsAccepted, ageVerified });
      toast.error(errorMsg);
      setErrorMessage(errorMsg);
      return;
    }

    if (!email || !password) {
      const errorMsg = 'Please enter email and password';
      log.warn('Sign up validation failed', { hasEmail: !!email, hasPassword: !!password });
      toast.error(errorMsg);
      setErrorMessage(errorMsg);
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      log.info('Creating Supabase account', { email });
      
      // Create the account immediately
      const authResult = await signUpUser(email, password, {
        ageVerified,
        termsAccepted
      });

      if (!authResult?.user) {
        throw new Error('Failed to create account');
      }

      log.info('Account created successfully', { 
        userId: authResult.user.id,
        email: authResult.user.email 
      });

      // If Supabase auto-confirms users, a session will be present.
      if ((authResult as any)?.session) {
        toast.success('Account created! Setting up your profile...');
        log.info('Signup completed with active session');
        
        // Manually trigger session refresh to ensure AuthContext picks up the new session
        try {
          await supabase.auth.refreshSession();
          log.info('Session refreshed after signup');
        } catch (refreshError) {
          log.error('Failed to refresh session after signup:', refreshError);
        }
        
        // Wait a moment for the auth subscription to process the session
        // This ensures AuthContext.user is set before Auth.tsx checks it
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Now navigate to / which will trigger RootRedirect logic
        log.info('Navigating to root after signup');
        navigate('/', { replace: true });
        return;
      }

      // Otherwise, email confirmation is required. Keep the user on this page
      // and rely on the confirmation link to complete auth via /auth/callback.
      toast.success('Account created! Please check your email to confirm your account.');
      log.info('Signup completed without active session (email confirmation likely required)');
      
    } catch (error: any) {
      log.error('Sign up error', { 
        errorMessage: error.message,
        errorCode: error.code
      });
      
      // Handle specific error cases
      if (error.message?.includes('already registered')) {
        setErrorMessage('This email is already registered. Please sign in instead.');
        toast.error('Email already registered');
      } else {
        setErrorMessage(error.message || 'Failed to create account');
        toast.error('Failed to create account. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSignUp} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <input 
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="w-full px-3 py-2 rounded bg-white/10 border border-white/10 text-white"
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="password">Create Password</Label>
          <input 
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Create a password"
            className="w-full px-3 py-2 rounded bg-white/10 border border-white/10 text-white"
            required
          />
        </div>
        
        <div className="mt-6 space-y-4 text-sm text-white/80">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="terms" 
              checked={termsAccepted}
              onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
              className="border-white/20"
            />
            <Label htmlFor="terms" className="text-sm">
              I agree to the{" "}
              <a 
                href="https://decoylabs.notion.site/Website-Terms-of-Service-1917374ef3cb802dba58ca483477ba42" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-primary hover:underline"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a 
                href="https://decoylabs.notion.site/Privacy-Policy-1917374ef3cb80fb98dbdb1774f39a1a" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-primary hover:underline"
              >
                Privacy Policy
              </a>
              <span className="text-red-500 ml-1">*</span>
            </Label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="age" 
              checked={ageVerified}
              onCheckedChange={(checked) => setAgeVerified(checked as boolean)}
              className="border-white/20"
            />
            <Label htmlFor="age" className="text-sm">
              I confirm that I am at least 13 years old
              <span className="text-red-500 ml-1">*</span>
            </Label>
          </div>

          {errorMessage && (
            <p className="text-red-500 text-sm p-3 bg-red-500/10 rounded border border-red-500/20">
              {errorMessage}
            </p>
          )}
        </div>
        
        <button 
          type="submit" 
          className="w-full py-2 bg-primary hover:bg-primary/90 text-white rounded font-medium transition-colors disabled:opacity-50"
          disabled={isLoading}
        >
          {isLoading ? "Creating account..." : "Create account"}
        </button>
        
        <p className="text-center text-sm text-white/60">
          Already have an account? <a href="#" onClick={() => navigate('/auth')} className="text-primary hover:underline">Sign in</a>
        </p>
      </form>
    </div>
  );
};
